/**
 * Page d'accueil : sélection des fichiers, création du partage.
 *
 * Une seule action principale — choisir des fichiers — puis une seule décision : envoyer.
 * Tout ce qui pourrait distraire (options, réglages) est volontairement absent.
 */

import { useState } from 'react';
import { FileText, Link2, Lock, Repeat } from 'react-feather';
import { useFormatters, useT } from '../i18n/index.js';
import { navigate } from '../router.js';
import { createShareLink } from '../session.js';
import { FileDrop } from '../ui/FileDrop.js';
import { HeroIllustration } from '../ui/HeroIllustration.js';
import { useParallax } from '../ui/useParallax.js';
import { useScrollProgress } from '../ui/useScrollProgress.js';

/** Nombre de fichiers listés avant de résumer le reste. */
const VISIBLE_FILES = 8;

/** Une intensité par étape, jamais identique : le mouvement reste discret et un peu
    organique plutôt que mécaniquement régulier. Appliqué au chiffre géant de l'étape. */
const EXPLAIN_INTENSITY_1 = 20;
const EXPLAIN_INTENSITY_2 = 16;
const EXPLAIN_INTENSITY_3 = 26;
const EXPLAIN_INTENSITY_4 = 18;

/** Les quatre étapes du mode d'emploi, donc les quatre stations du câble. */
const EXPLAIN_STEPS = 4;

export function Home(): React.JSX.Element {
  const [files, setFiles] = useState<File[]>([]);
  const translator = useT();
  // Un hook par étape (nombre fixe, connu à l'avance) : la liste des règles de React sur
  // les hooks interdit de les appeler dans la boucle `.map()` plus bas.
  const parallaxRef1 = useParallax<HTMLSpanElement>(EXPLAIN_INTENSITY_1);
  const parallaxRef2 = useParallax<HTMLSpanElement>(EXPLAIN_INTENSITY_2);
  const parallaxRef3 = useParallax<HTMLSpanElement>(EXPLAIN_INTENSITY_3);
  const parallaxRef4 = useParallax<HTMLSpanElement>(EXPLAIN_INTENSITY_4);
  // Progression du défilement dans la liste : pilote la descente du paquet le long du
  // câble et l'allumage des stations à son passage.
  const { ref: cableRef, reached } = useScrollProgress<HTMLOListElement>(EXPLAIN_STEPS);
  const format = useFormatters();

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const hidden = files.length - VISIBLE_FILES;

  const addFiles = (incoming: File[]): void => {
    setFiles((current) => [...current, ...incoming]);
  };

  /** Par défaut, le lien ne sert qu'une fois. Voir le commentaire du commutateur. */
  const [allowMultiple, setAllowMultiple] = useState(false);

  const startSharing = (): void => {
    if (files.length === 0) return;
    // `createShareLink` conserve les objets `File` en mémoire et marque la salle comme
    // créée par cet onglet — nécessaire pour distinguer un rechargement d'une ouverture
    // de lien par un correspondant.
    // La politique est transmise à la création : c'est le seul moment où elle peut
    // être décidée. Le serveur la fige ensuite, et un second venu ne peut pas la relâcher.
    const { path } = createShareLink(files, allowMultiple ? 'multi' : 'one-shot');
    navigate(path);
  };

  return (
    <div className="stack home-stack">
      <section className="hero-grid">
        <div className="hero-content">
          <span className="hero-eyebrow">{translator.t('home.tag')}</span>
          <h1 className="sr-only">{translator.t('home.title')}</h1>
          <p className="hero-title" aria-hidden="true">
            <span className="hero-title-line hero-title-soft">
              {translator.t('home.titleLine1')}
            </span>
            <span className="hero-title-line hero-title-strong">
              {translator.t('home.titleLine2')}
            </span>
          </p>
          {/* La ligne d'accroche visuelle (« SIMPLE · PRIVÉ · SANS LIMITE ») a cédé la
              place au visuel de marque dans cette mise en page ; le vrai descriptif reste
              lu par les lecteurs d'écran. */}
          <p className="lede sr-only">{translator.t('home.lede')}</p>

          <div className="hero-drop-wrap">
            <FileDrop onFiles={addFiles} />

            {files.length > 0 && (
              <div className="card">
                <div className="progress-head">
                  <h2 style={{ margin: 0 }}>
                    {translator.plural('home.filesReady', files.length)}
                  </h2>
                  <span className="faint">{format.bytes(totalBytes)}</span>
                </div>

                <ul className="file-list">
                  {files.slice(0, VISIBLE_FILES).map((file, index) => (
                    <li className="file-item" key={`${file.name}-${file.size}-${index}`}>
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                      <span className="file-size">{format.bytes(file.size)}</span>
                    </li>
                  ))}
                  {hidden > 0 && (
                    <li className="file-item">
                      <span className="file-name muted">
                        {translator.plural('home.andMore', hidden)}
                      </span>
                    </li>
                  )}
                </ul>

                {/*
                  Politique d'usage du lien. Éteint par défaut : le lien meurt après son
                  premier destinataire. C'est le choix sûr — un lien qui fuit (historique,
                  presse-papiers, conversation) reste sinon exploitable jusqu'à expiration.

                  L'utilisateur doit pouvoir voir ce choix **avant** de créer le lien :
                  c'est lui qui décide de la durée de vie du secret qu'il s'apprête à
                  transmettre.
                */}
                {files.length > 0 && (
                  <label className="policy-toggle">
                    <input
                      type="checkbox"
                      checked={allowMultiple}
                      onChange={(event) => setAllowMultiple(event.target.checked)}
                    />
                    <span>
                      <strong>{translator.t('home.allowMultiple')}</strong>
                      <span className="muted policy-hint">
                        {allowMultiple
                          ? translator.t('home.allowMultipleOn')
                          : translator.t('home.allowMultipleOff')}
                      </span>
                    </span>
                  </label>
                )}

                <div className="row" style={{ marginTop: '1.25rem' }}>
                  <button type="button" className="btn btn-primary" onClick={startSharing}>
                    {translator.t('home.createLink')}
                  </button>
                  <button type="button" className="btn" onClick={() => setFiles([])}>
                    {translator.t('home.clearAll')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Purement ornementale — masquée en petit écran (voir `styles.css`), où il n'y a
            pas la place de la montrer sans écraser le formulaire. */}
        <div className="hero-visual">
          <HeroIllustration />
        </div>
      </section>

      {/* Ce que la page ne montre qu'en un coup d'œil (« sans serveur », « chiffré ») se
          nomme ici précisément : de quoi convaincre qui veut vérifier plutôt que croire
          sur parole, sans pour autant transformer l'accueil en fiche technique. Avant le
          mode d'emploi : la confiance se gagne avant d'expliquer comment s'en servir. */}
      <section className="tech-section">
        <span className="hero-eyebrow">{translator.t('home.techLabel')}</span>
        <ul className="tech-row">
          {[
            { value: translator.t('home.tech1Value'), label: translator.t('home.tech1Label') },
            { value: translator.t('home.tech2Value'), label: translator.t('home.tech2Label') },
            { value: translator.t('home.tech3Value'), label: translator.t('home.tech3Label') },
            { value: translator.t('home.tech4Value'), label: translator.t('home.tech4Label') },
          ].map(({ value, label }, index) => (
            <li className="tech-item" key={index}>
              <span className="tech-value">{value}</span>
              <span className="tech-label">{label}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Le mode d'emploi, en quatre étapes courtes — ce que l'interface elle-même ne dit
          pas déjà (déposer, glisser, coller), c'est ce qui la rend digne de confiance.

          Les quatre étapes ne sont pas quatre cases : c'est **un seul trajet**. Un câble
          pointillé traverse toute la liste, les étapes s'y accrochent comme des stations,
          et un paquet citron le descend au rythme du défilement, allumant chaque station
          à son passage. Le pointillé reprend celui de l'arc du héros
          (`HeroIllustration.tsx`) qui porte déjà un fichier d'un appareil à l'autre : la
          page raconte la même chose en haut et en bas. Le trait est en encre et non en
          citron, à la différence de l'arc : sur toute la hauteur d'une section il tient un
          rôle de structure, et le citron doit rester ce qui *circule* — le paquet.

          Câble et paquet sont frères des `<li>` et non dedans : l'`overflow: hidden` de
          chaque bandeau les couperait à chaque étape, là où ils doivent justement être ce
          qui les relie. */}
      <section className="explain-section">
        <span className="hero-eyebrow">{translator.t('home.explainLabel')}</span>

        <ol className="explain-row" ref={cableRef}>
          <svg className="explain-cable" aria-hidden="true">
            <line
              x1="50%"
              y1="0"
              x2="50%"
              y2="100%"
              stroke="var(--border)"
              strokeWidth="3"
              strokeDasharray="2 14"
              strokeLinecap="round"
            />
          </svg>
          <div className="explain-packet" aria-hidden="true" />

          {[
            {
              title: translator.t('home.explain1Title'),
              body: translator.t('home.explain1Body'),
              Icon: FileText,
              figureRef: parallaxRef1,
            },
            {
              title: translator.t('home.explain2Title'),
              body: translator.t('home.explain2Body'),
              Icon: Link2,
              figureRef: parallaxRef2,
            },
            {
              title: translator.t('home.explain3Title'),
              body: translator.t('home.explain3Body'),
              Icon: Repeat,
              figureRef: parallaxRef3,
            },
            {
              title: translator.t('home.explain4Title'),
              body: translator.t('home.explain4Body'),
              Icon: Lock,
              figureRef: parallaxRef4,
            },
          ].map(({ title, body, Icon, figureRef }, index) => (
            <li className="explain-step" key={index}>
              {/* Le glyphe de l'étape, en très grand et presque effacé, posé dans le fond
                  du bandeau : au passage du paquet il s'éclaire en citron — l'événement se
                  joue à l'échelle de la bande entière plutôt que dans une pastille. */}
              <div className="explain-glyph" data-reached={index < reached} aria-hidden="true">
                <Icon size="100%" strokeWidth={1} />
              </div>
              <span className="explain-figure" ref={figureRef} aria-hidden="true">
                {index + 1}
              </span>
              <div className="explain-station" data-reached={index < reached} aria-hidden="true" />
              <div className="explain-inner">
                <div className="explain-copy">
                  <span className="explain-title">{title}</span>
                  <span className="explain-body">{body}</span>
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* Le condensé de la fiche technique qui précède cette section : plutôt que
            répéter AES-256 / SHA-256 / P2P, une seule pastille qui dit ce qui compte —
            personne d'autre ne voit passer les fichiers. */}
        <p className="no-server-badge">
          <span>{translator.t('home.badgeLine1')}</span>
          <span>{translator.t('home.badgeLine2')}</span>
        </p>
      </section>
    </div>
  );
}
